<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import type { SourceExtendDialogMeter } from "@core/components/source/meter/form/extend/SourceExtendDialog.vue"

// ================================
// TYPES
// ================================

type ConsentPayload = {
    consentId?: string
    sourceId?: string
    token?: string
}

// ================================
// PROPS & EMITS
// ================================

defineProps<{
    meter: SourceExtendDialogMeter
    buildingId: string
}>()

const emit = defineEmits<{
    cancel: []
    done: []
    "update:consent": [payload: ConsentPayload]
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// STATE
// ================================

const skipVisibilityCancel = ref(false)

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            dialog_header: "Changement de titulaire",
        },
    },
})

// ================================
// HANDLERS
// ================================

function onDialogVisibleUpdate(v: boolean): void {
    if (v) return
    if (skipVisibilityCancel.value) {
        skipVisibilityCancel.value = false
        return
    }
    emit("cancel")
}

function onFormCancel(): void {
    visible.value = false
    emit("cancel")
}

function onFormComplete(payload: {
    consentRequestId?: string
    sourceId?: string
}): void {
    emit("update:consent", {
        consentId: payload.consentRequestId,
        sourceId: payload.sourceId,
    })
    skipVisibilityCancel.value = true
    visible.value = false
    emit("done")
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        position="top"
        :header="t('dialog_header')"
        :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        :pt="{
            content:
                'max-h-[calc(100dvh-10rem)] overflow-y-auto overflow-x-hidden flex justify-center items-start w-full py-2',
        }"
        @update:visible="onDialogVisibleUpdate"
    >
        <SourceChangeOwnerForm
            class="w-full max-w-3xl shrink-0 py-2"
            :meter="meter"
            @cancel="onFormCancel"
            @complete="onFormComplete"
        />
    </BaseDialog>
</template>
